import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {
  getQuestionnaireTemplateConfig,
  QuestionnaireTemplateConfig,
} from 'src/app/constants/questionnaire-template-registry';
import { QuestionnaireHistoryItem } from 'src/app/interfaces/Questionnaire/IQuestionnaireHistory';
import { date_format, datetime_format } from 'src/date/date.configuration';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { PatientService } from 'src/app/services/patient.service';
import {
  QuestionnaireNavigationService,
  QuestionnaireScreenMode,
} from 'src/app/services/questionnaireNavigation.service';
import { getQuestionnaireActorDisplayValue } from 'src/app/utils/questionnaire-history-actor';
import { getQuestionnaireScoreState } from 'src/app/utils/questionnaire-history-score';

@Component({
  selector: 'app-questionnaire-history-detail',
  templateUrl: './questionnaire-history-detail.component.html',
  styleUrls: ['./questionnaire-history-detail.component.css'],
  standalone: false,
})
export class QuestionnaireHistoryDetailComponent implements OnInit, OnChanges {
  @Input() patientId?: string;
  @Input() eocId?: string;
  @Input() visitId?: string;
  @Input() submissionId?: string;
  @Input() questionnaireTemplateId?: number;
  @Input() historyItem?: QuestionnaireHistoryItem | null;
  @Input() embeddedMode = false;
  @Input() screenModeOverride?: QuestionnaireScreenMode;
  @Output() backRequested = new EventEmitter<void>();

  isLoading = false;
  error = '';
  item?: QuestionnaireHistoryItem;
  mode: QuestionnaireScreenMode = 'profile-readonly';

  constructor(
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly patientService: PatientService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly questionnaireNavigationService: QuestionnaireNavigationService,
  ) {}

  ngOnInit(): void {
    this.scrollToTop();
    this.loadSubmission();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['patientId']?.previousValue !==
        changes['patientId']?.currentValue ||
      changes['submissionId']?.previousValue !==
        changes['submissionId']?.currentValue ||
      changes['questionnaireTemplateId']?.previousValue !==
        changes['questionnaireTemplateId']?.currentValue ||
      changes['screenModeOverride']?.previousValue !==
        changes['screenModeOverride']?.currentValue
    ) {
      this.scrollToTop();
      this.loadSubmission();
    }
  }

  loadSubmission(): void {
    const templateId = this.questionnaireTemplateId;
    this.mode = this.resolveMode();
    this.questionnaireNavigationService.setTemplateContext(templateId);

    if (this.mode === 'visit-add') {
      this.item = undefined;
      this.error = templateId
        ? ''
        : this.translate.instant('Could not load questionnaire submission.');
      this.isLoading = false;
      return;
    }

    if (!this.submissionId || !templateId) {
      return;
    }

    this.isLoading = true;
    this.error = '';

    this.questionnaireService
      .getQuestionnaireDetail(this.submissionId)
      .subscribe({
        next: (item) => {
          if (!item) {
            this.error = this.translate.instant(
              'Could not load questionnaire submission.',
            );
            this.item = undefined;
          } else {
            this.setDetailItem(item);
          }

          this.isLoading = false;
        },
        error: () => {
          this.error = this.translate.instant(
            'Could not load questionnaire submission.',
          );
          this.item = undefined;
          this.isLoading = false;
        },
      });
  }

  backToHistory(): void {
    if (this.embeddedMode) {
      this.backRequested.emit();
      return;
    }

    this.questionnaireService.invalidateQuestionnaireOverviewCache();
    this.questionnaireNavigationService.requestReturnToSection(
      this.questionnaireTemplateId,
    );
    const visitCommands = this.getVisitOverviewRouteCommands();

    if (visitCommands) {
      this.router.navigate(visitCommands);
      return;
    }

    const commands = this.getProfileOverviewRouteCommands();

    this.router.navigate(commands);
  }

  getQuestionnaireTitle(): string {
    return (
      this.item?.questionnaire?.title ||
      this.translate.instant(
        this.getTemplateConfig()?.title ?? 'Questionnaire details',
      )
    );
  }

  getStatus(): string {
    if (this.item?.status) {
      return this.item.status;
    }

    if (this.item?.isSubmitted !== undefined) {
      return this.item.isSubmitted ? 'Submitted' : 'Draft';
    }

    return 'N/A';
  }

  getVisitContext(): string {
    if (!this.item) {
      return 'N/A';
    }

    const visit = this.item.visitLabel ?? this.item.visitId?.toString();
    const episode =
      this.item.episodeCareLabel ?? this.item.episodeCareId?.toString();

    if (visit && episode) {
      return `Visit ${visit} / EOC ${episode}`;
    }

    if (visit) {
      return `Visit ${visit}`;
    }

    if (episode) {
      return `EOC ${episode}`;
    }

    return 'N/A';
  }

  getCreatedDate(): string {
    return this.formatDate(
      this.item?.createdDate ?? this.item?.creationDate,
      false,
    );
  }

  getSubmittedDate(): string {
    return this.formatDate(
      this.item?.submittedDate ??
        this.item?.lastUpdateDate ??
        this.item?.createdDate ??
        this.item?.creationDate,
      true,
    );
  }

  getActor(): string {
    return this.item ? getQuestionnaireActorDisplayValue(this.item) : 'N/A';
  }

  shouldShowScoreSummary(): boolean {
    if (!this.item) {
      return false;
    }

    const scoreState = getQuestionnaireScoreState(this.item);
    return scoreState.kind === 'scored' || scoreState.kind === 'incomplete';
  }

  getScoreSummary(): string {
    if (!this.item) {
      return 'N/A';
    }

    const scoreState = getQuestionnaireScoreState(this.item);

    if (scoreState.kind === 'scored') {
      return String(scoreState.value);
    }

    if (scoreState.kind === 'incomplete') {
      return scoreState.label;
    }

    return 'N/A';
  }

  canRenderQuestionnaire(): boolean {
    return !!this.item?.questionnaire?.pages?.length;
  }

  canRenderQuestionnaireScreen(): boolean {
    return this.mode === 'visit-add' || this.canRenderQuestionnaire();
  }

  isReadonlyMode(): boolean {
    return this.mode === 'profile-readonly';
  }

  shouldUseHistoricalAnswers(): boolean {
    return this.mode !== 'visit-add' && !!this.item;
  }

  shouldStartBlank(): boolean {
    return this.mode === 'visit-add';
  }

  shouldShowSummary(): boolean {
    return this.mode !== 'visit-add' && !!this.item;
  }

  shouldReturnToHistoryAfterSubmit(): boolean {
    return this.mode !== 'profile-readonly';
  }

  handleQuestionnaireSubmitted(): void {
    this.questionnaireService.invalidateQuestionnaireOverviewCache();
    this.backToHistory();
  }

  private resolvePatientId(): string {
    return this.patientId ?? this.patientService.patientIDsignal();
  }

  private setDetailItem(item: QuestionnaireHistoryItem): void {
    this.item = item;
    this.error = this.canRenderQuestionnaire()
      ? ''
      : this.translate.instant('Could not load questionnaire submission.');
    this.isLoading = false;
  }

  private getTemplateConfig(): QuestionnaireTemplateConfig | undefined {
    return getQuestionnaireTemplateConfig(this.questionnaireTemplateId);
  }

  private formatDate(value?: string, includeTime: boolean = false): string {
    if (!value) {
      return 'N/A';
    }

    const parsed = moment(
      value,
      [moment.ISO_8601, datetime_format, date_format],
      true,
    );
    return parsed.isValid()
      ? parsed.format(includeTime ? datetime_format : date_format)
      : value;
  }

  private resolveMode(): QuestionnaireScreenMode {
    if (this.screenModeOverride) {
      return this.screenModeOverride;
    }

    if (
      this.router.url.includes('episodeOfCare') ||
      this.router.url.includes('inPatientView')
    ) {
      return this.router.url.endsWith('/add') ? 'visit-add' : 'visit-edit';
    }

    return 'profile-readonly';
  }

  private getVisitOverviewRouteCommands(): string[] | null {
    const patientId = this.resolvePatientId();
    const eocId = this.eocId?.toString();
    const visitId = this.visitId?.toString();

    if (!patientId || !eocId || !visitId) {
      return null;
    }

    const basePath = this.router.url.includes('inPatientView')
      ? 'inPatientView'
      : this.router.url.includes('episodeOfCare')
        ? 'episodeOfCare'
        : null;

    if (!basePath) {
      return null;
    }

    return [
      '/ehr',
      basePath,
      patientId,
      eocId,
      visitId,
    ];
  }

  private getProfileOverviewRouteCommands(): string[] {
    const patientId = this.resolvePatientId();
    const commands = ['/ehr', 'profile', patientId];

    if (this.eocId) {
      commands.push(this.eocId);
    }

    return commands;
  }

  private scrollToTop(): void {
    setTimeout(() => {
      window.scroll({
        top: 0,
        behavior: 'auto',
      });
    });
  }
}
